import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Spinner } from 'styles/components/Spinner';
import { StyledTable, StyledRow, StyledCell, StyledHeader } from 'styles/components/Table.styles';
import { DragHandleIcon, EditIcon, PlusIcon, TrashIcon } from 'styles/components/Icons';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { DraggableCell } from './DraggableCell';
import T from 'mrf/utils/T';
import useMRFTranslation from 'mrf/utils/useMRFTranslation';
import { useSelector } from 'react-redux';
import Tooltip from './Tooltip';
import { generateHtmlId } from 'mrf/utils/StringGenerators';
import ErrorTip from 'mrf/view/ErrorTip';
import IF from 'mrf/view/IF';
import mrf from 'mrf.config';

ObjectTable.propTypes = {
    header: PropTypes.bool, // Whether or not to render header.
    footer: PropTypes.bool, // TODO: ??
    list: PropTypes.array, // List of object ids to be rendered.
    byId: PropTypes.object, // Object containing the mapping from id to object.
    fields: PropTypes.array.isRequired, // List describing how each field should be rendered.
    movable: PropTypes.bool, // Whether or not the user can move elements from this list.
    addCb: PropTypes.func, // Callback when the add button is clicked.
    editCb: PropTypes.func, // Callback when the edit button is clicked.
    removeCb: PropTypes.func, // Callback when the remove button is clicked.
    moveElementCb: PropTypes.func, // Callback for when a row is moved.
    onClick: PropTypes.func, // Callback for when a row is clicked on.
    onSort: PropTypes.func, // Callback for when a hader is clicked on.
    sortedByIdx: PropTypes.number, // Index of the sorted column
    sortedByDir: PropTypes.string, // Direction of the sort 'asc'/'desc'
    errors: PropTypes.array, // Array containing all errors of this list.
    descriptor: PropTypes.object,
    loading: PropTypes.bool,
    emptyListLabel: PropTypes.string,
    messageOverride: PropTypes.string, // Show this message instead of the list.
};

const renderErrorCell = (fieldKey, idx, obj, resource, field, data, fieldDescriptor, debug) => {
    if (debug) {
        console.log("---------------------");
        console.log("Unable to render cell (fieldKey=" + fieldKey + ", idx=" + idx + ")");
        console.log("Field keys: " + field.keys?.join("."));
        console.log("Field Descriptor: " + fieldDescriptor);
        console.log("obj: " + obj);
        console.log("resource: " + resource);
        console.log("Data: " + data);
    }

    if (mrf.production) {
        return false;
    } else {
        return <span className='text-red-500'>ERROR</span>;
    }
}

const RenderNestedCell = ({ idx, fieldKey, obj, field, fieldDescriptor, i = 0, debug }) => {
    const resource = useSelector(state => state.resources?.[fieldDescriptor?.resource]?.byId?.[obj]);

    if (field.keys && i + 1 < field.keys.length) {
        fieldKey = field.keys?.[++i];
        obj = resource?.[fieldKey] ?? resource?.[fieldKey + 'Id'] ?? obj?.[fieldKey];

        return <RenderNestedCell idx={idx} fieldKey={fieldKey} obj={obj} field={field} fieldDescriptor={fieldDescriptor?.descriptor?.[fieldKey]} i={i} />
    }

    const data = fieldDescriptor?.hasOwnProperty(fieldKey)
        ? resource
        : obj;

    return (
        field.map
            ? field.map(data, idx)
            : data
    ) ?? renderErrorCell(fieldKey, idx, obj, resource, field, data, fieldDescriptor, debug);
}

const RenderCell = ({ idx, obj, field, descriptor, debug }) => {
    const t = useMRFTranslation();

    if (!obj) {
        return null;
    }

    if (field.keys) {
        const fieldKey = field.keys?.[0];
        return <RenderNestedCell
            idx={idx}
            field={field}
            fieldKey={fieldKey}
            obj={obj?.[fieldKey] ?? obj?.[fieldKey + 'Id']}
            fieldDescriptor={descriptor?.[fieldKey]}
            debug={debug}
        />
    }

    if (!field.key) {
        return (obj && field.map(obj, idx)) ?? null;
    }

    if (descriptor?.hasOwnProperty(field.key)) {
        return <RenderNestedCell
            idx={idx}
            field={field}
            fieldKey={field.key}
            obj={obj?.[field.key] ?? obj?.[field.key + 'Id']}
            fiedlDescriptor={descriptor?.[field.key]}
            debug={debug}
        />
    }

    let fieldNestedValue = obj[field.key];

    if (fieldNestedValue && field.translate) {
        const translationTerm =
            (typeof field.translate === 'string'
                ? field.translate
                : 'translation')
            + ':' + fieldNestedValue;

        fieldNestedValue = t(translationTerm);
    }

    return (field.map
        ? field.map(fieldNestedValue)
        : fieldNestedValue) ?? null;
}

export function ObjectTable({
    header = true,
    footer = false,
    list,
    byId,

    loading,

    fields,
    descriptor,
    addCb,
    editCb,
    field: formField,
    removeCb,
    movable = false,
    moveElementCb,

    onClick,
    onSort,
    sortedByIdx,
    sortedByDir,
    errors,
    htmlId,

    emptyListLabel = 'This table is empty',
    messageOverride,
    debug = false,
    readOnly,
}) {

    const onDragEnd = result => {
        const { destination, source } = result;

        if (!destination || (destination.droppableId === source.droppableId && destination.index === source.index)) return;

        if (moveElementCb) {
            moveElementCb(source.index, destination.index);
        }
    };

    const hasControllers = editCb || removeCb;
    const cols = fields.length + !!movable + !!hasControllers;
    const isClickable = onClick ? true : false;

    if (debug) {
        console.log("####################")
        console.log('form field:' + formField);
        console.log('fields:');
        console.log(fields);
        console.log("list:");
        console.log(list);
        console.log("Descriptor:");
        console.log(descriptor);
    }

    const spinnerWrapper = useMemo(() =>
        <StyledRow>
            <StyledCell colSpan={cols}>
                <Spinner />
            </StyledCell>
        </StyledRow>,
        [cols]
    );
    const messageOverrideWrapper = useMemo(() =>
        <StyledRow align='center'>
            <StyledCell colSpan={cols}>
                <T>{messageOverride}</T>
            </StyledCell>
        </StyledRow>,
        [cols, messageOverride]
    );

    const emptyListLabelWrapper = useMemo(() =>
        <StyledRow align='center'><StyledCell colSpan={cols}><T>{emptyListLabel}</T></StyledCell></StyledRow>,
        [cols, emptyListLabel]
    );

    return <>
        {!readOnly ? <>
            <StyledTable footer={!readOnly && footer} header={header} error={errors} id={htmlId} isClickable={isClickable}>
                {header && (
                    <thead>
                        <StyledRow>
                            {!readOnly && movable && <th />}
                            {fields.map((field, idx) => (
                                <StyledHeader
                                    key={idx}
                                    onClick={field.sortable ? () => onSort?.(idx) : undefined}
                                    isClickable={onSort && field.sortable}
                                >
                                    <T>{field.name}</T>
                                    <IF condition={idx === sortedByIdx}>{ sortedByDir === 'asc' ? '↓' : '↑' }</IF>
                                    <IF condition={idx !== sortedByIdx && field.sortable}>↕</IF>
                                </StyledHeader>
                            ))}
                            {!readOnly && hasControllers && <th />}
                        </StyledRow>
                    </thead>
                )}
                <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId='table'>
                        {(provided1) => (
                            <tbody
                                ref={provided1.innerRef}
                                {...provided1.droppableProps}
                            >
                                <IF condition={!loading} alt={spinnerWrapper}>
                                    <IF condition={!messageOverride} alt={messageOverrideWrapper}>
                                        <IF condition={list?.length !== 0} alt={emptyListLabelWrapper}>{
                                            list?.map((obj, idx1) => {
                                                return <Draggable
                                                    key={idx1}
                                                    draggableId={idx1.toString()}
                                                    index={idx1}
                                                    isDragDisabled={!movable}
                                                >
                                                    {(provided2, snapshot) => (
                                                        <StyledRow
                                                            ref={provided2.innerRef}
                                                            isDragging={snapshot.isDragging}
                                                            onClick={() => onClick?.(obj, idx1, byId?.[obj])}
                                                            error={errors?.[idx1]}
                                                            id={generateHtmlId(htmlId, `row_${idx1}`)}
                                                            {...provided2.draggableProps}
                                                        >
                                                            {!readOnly && movable &&
                                                                <DraggableCell
                                                                    isDragOccurring={snapshot.isDragging}
                                                                    className="drag"
                                                                    {...provided2.dragHandleProps}
                                                                >
                                                                    <DragHandleIcon size="24" />
                                                                </DraggableCell>}
                                                            {fields.map((field, idx2) => (
                                                                <DraggableCell
                                                                    isDragOccurring={snapshot.isDragging}
                                                                    key={idx2}
                                                                    align={field.align}
                                                                >
                                                                    <RenderCell
                                                                        idx={idx1}
                                                                        obj={byId ? byId[obj] : obj}
                                                                        field={field}
                                                                        descriptor={descriptor}
                                                                        debug={debug}
                                                                    />
                                                                </DraggableCell>
                                                            ))}
                                                            {!readOnly && hasControllers &&
                                                                <DraggableCell
                                                                    isDragOccurring={snapshot.isDragging}
                                                                    align='right'
                                                                >
                                                                    {editCb &&
                                                                        <Tooltip content='Edit'>
                                                                            <EditIcon id={`${htmlId}_${formField}_edit_${idx1}`} size={30} onClick={() => editCb(idx1)} />
                                                                        </Tooltip>
                                                                    }
                                                                    {removeCb &&
                                                                        <Tooltip content='Remove'>
                                                                            <TrashIcon id={`${htmlId}_${formField}_remove_${idx1}`} size={30} onClick={() => removeCb(idx1)} />
                                                                        </Tooltip>
                                                                    }
                                                                </DraggableCell>
                                                            }
                                                        </StyledRow>
                                                    )}
                                                </Draggable>
                                            })
                                        }</IF>
                                    </IF>
                                    <IF condition={!readOnly && addCb}>
                                        <StyledRow align='right'>
                                            <StyledCell colSpan={cols}>
                                                <Tooltip content='Add'>
                                                    <PlusIcon size={30} onClick={addCb} id={generateHtmlId(htmlId, `${formField}_add_button`)} />
                                                </Tooltip>
                                            </StyledCell>
                                        </StyledRow>
                                    </IF>
                                </IF>
                                {provided1.placeholder}
                            </tbody>
                        )}
                    </Droppable>
                </DragDropContext>
            </StyledTable>
        </> : <>
            <div className='prose max-w-none'>
                <table>
                    <thead>
                        <tr>
                            {fields?.map?.((field, idx) => (
                                <th key={idx}>
                                    <T>{field.name}</T>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {list?.map?.((obj, idx1) => <tr key={idx1}>
                            {fields.map((field, idx2) => (
                                <td key={idx2}>
                                    <RenderCell
                                        idx={idx1}
                                        obj={byId ? byId[obj] : obj}
                                        field={field}
                                        byId={byId}
                                        descriptor={descriptor}
                                    />
                                </td>
                            ))}
                        </tr>)}
                    </tbody>
                </table>
            </div>
        </>}
        {!readOnly && errors && <ErrorTip message={'Some lines contain errors.'} />}
    </>;
}
